// The passkey (WebAuthn) platform seam (docs/09 §9). ONE account implementation
// serves three hosts, so the ceremony itself is injected: iOS runs it over the
// ASAuthorization `__host` seam, Android over the androidx Credential Manager
// Capacitor plugin, the web over `navigator.credentials`.
//
// Why the PRF salts are passed as BYTES rather than pre-merged into the options
// JSON: the two platforms disagree on the encoding. The native seams want
// base64url strings under `extensions.prf.eval` (their JSON goes verbatim into
// ASAuthorization / Credential Manager); the browser wants `BufferSource`s.
// Keeping that split inside each provider is what lets BrainAccount stay free of
// both encodings — it deals only in raw PRF outputs.
//
// The server NEVER sees a PRF output (docs/03 §2): providers return it to the
// caller, and BrainAccount strips it from the credential before anything is
// posted.

import type { WebAuthnJson } from '@merkury/protocol';
import { fromBase64, toBase64 } from '../lib/bytes.js';

/** base64url (RFC 4648 §5, no padding) — the encoding every binary WebAuthn
 *  field uses on the wire, on every platform. lib/bytes' toBase64/fromBase64
 *  are standard PADDED base64, so the two are not interchangeable. */
export function bytesToB64url(bytes: Uint8Array): string {
  return toBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function b64urlToBytes(s: string): Uint8Array {
  const padded = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4);
  return fromBase64(padded);
}

/** One ceremony's inputs: the server's opaque options plus the salts to
 *  evaluate PRF over. `options` may be go-webauthn's `{publicKey:{…}}` wrapper
 *  or the bare object — every provider unwraps it. */
export interface PasskeyCeremonyRequest {
  options: WebAuthnJson;
  /** `first` is the fixed app-wide restore salt; `second` the account's legacy
   *  per-user salt, evaluated in the SAME ceremony so old blobs still restore. */
  prf?: { first: Uint8Array; second?: Uint8Array };
}

/** One ceremony's outputs. `credential` still carries the PRF results — the
 *  caller strips them before the credential leaves the device. */
export interface PasskeyCeremonyResult {
  credential: WebAuthnJson;
  /** PRF output over the `first` salt; absent ⇒ the authenticator has no PRF. */
  prfOutput?: Uint8Array;
  /** PRF output over the `second` salt, when one was requested and returned. */
  prfOutputSecond?: Uint8Array;
  /** The authenticator acknowledged the PRF extension (registration only —
   *  create-time PRF *results* are unreliable, so this is the signal to harvest
   *  the output with a follow-up local assertion). */
  prfEnabled: boolean;
}

/**
 * The platform seam. `create`/`assert` resolve `undefined` for "the ceremony
 * ran and produced no credential" (empty discoverable set, nothing chosen) —
 * NOT an error. A user dismissal or a platform failure REJECTS, with a stable
 * `.code` from `PASSKEY_ERROR_CODES` when the platform could classify it
 * (docs/09 §8 — codes cross the boundary, never message text).
 */
export interface PasskeyProvider {
  /** True when this host can run ceremonies at all. Hosts answer for
   *  themselves: the Capacitor WebView has no `navigator.credentials` WebAuthn
   *  yet counts as supported because the native plugin speaks the same JSON. */
  supported(): boolean;
  create(req: PasskeyCeremonyRequest): Promise<PasskeyCeremonyResult | undefined>;
  assert(req: PasskeyCeremonyRequest): Promise<PasskeyCeremonyResult | undefined>;
}

/**
 * The coded rejections a provider may stamp on `.code`. The first four are the
 * native seams' own vocabulary (ios/BOUNDARY.md §2.11, Android's
 * `MerkuryPasskeysPlugin`); `wrong-origin` is web-only — a browser without
 * WebAuthn Related Origin Requests support rejecting a ceremony because the
 * page is on a mirror origin, which the UI must word differently ("switch to
 * the primary domain") from an ordinary cancellation.
 */
export const PASSKEY_ERROR_CODES = [
  'cancelled',
  'no-credential',
  'unsupported',
  'wrong-origin',
  'error',
] as const;

export type PasskeyErrorCode = (typeof PASSKEY_ERROR_CODES)[number];

const CODE_SET: ReadonlySet<string> = new Set<string>(PASSKEY_ERROR_CODES);

/** Normalize a thrown value to an Error carrying `.code` when one is knowable.
 *  Shared by the providers so consumers branch one way on every platform. */
export function withPasskeyCode(err: unknown, code?: string): Error & { code?: string } {
  const e = (err instanceof Error ? err : new Error(String(err))) as Error & { code?: string };
  if (e.code === undefined) {
    if (code !== undefined && CODE_SET.has(code)) e.code = code;
    // The iOS host replies `{error:"<code>"}`, which host.ts turns into an
    // Error with the code on `.message` — recover it.
    else if (CODE_SET.has(e.message)) e.code = e.message;
  }
  return e;
}

/** Unwrap go-webauthn's `{publicKey:{…}}` to the bare options object every
 *  platform ceremony actually consumes. */
export function unwrapPublicKey(options: WebAuthnJson): Record<string, unknown> {
  const j = options as { publicKey?: unknown };
  return (j.publicKey ?? options) as Record<string, unknown>;
}
